import React, { useEffect, useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPhone } from "@fortawesome/free-solid-svg-icons";
import { faWhatsapp } from "@fortawesome/free-brands-svg-icons";
import { cdnAssetUrl } from "../../config/staticAssets";
import { useMarketingLabels } from "./useMarketingLabels";

const missionCards = [
  {
    title: "Our Mission",
    text: "Connect all the members globally to find better solutions for career path",
    icon: cdnAssetUrl("assets/img/icon/ms_01.png"),
  },
  {
    title: "Our Vision",
    text: "Create a comprehensive opportunity for every workforce in the world",
    icon: cdnAssetUrl("assets/img/icon/ms_02.png"),
  },
];

const featureCards = [
  {
    title: "Technology",
    text: "Including roles in software development, IT support, and cybersecurity.",
    icon: cdnAssetUrl("assets/img/icon/tech-icon1.png"),
    className: "wlj-about-feature-item--offset-1",
  },
  {
    title: "Facility Management",
    text: "Including role in Production Manager, MEP Supervisor, Soft Services Manager, Maintenance Manager.",
    icon: cdnAssetUrl("assets/img/icon/healthcare1.png"),
    className: "wlj-about-feature-item--offset-2",
  },
  {
    title: "Education",
    text: "Teaching positions, administrative roles, and educational support staff.",
    icon: cdnAssetUrl("assets/img/icon/education1.png"),
    className: "wlj-about-feature-item--offset-3",
  },
  {
    title: "Search More",
    text: "",
    icon: cdnAssetUrl("assets/img/icon/search1.png"),
    href: "https://welinkjobs.com/SearchJob",
    className: "",
  },
];

const serviceCards = [
  {
    title: "Company Profiles:",
    text: "Employers can create detailed profiles to attract potential candidates",
    icon: cdnAssetUrl("assets/img/icon/srv_01.svg"),
  },
  {
    title: "Resume Building:",
    text: "Tools and templates to help users create professional resumes",
    icon: cdnAssetUrl("assets/img/icon/srv_03.svg"),
  },
  {
    title: "Job Alerts:",
    text: "Personalized job alerts based on user preferences and search history",
    icon: cdnAssetUrl("assets/img/icon/srv_02.svg"),
  },
  {
    title: "Networking:",
    text: "Opportunities to connect with industry professionals and other job seekers",
    icon: cdnAssetUrl("assets/img/icon/srv_04.svg"),
  },
  {
    title: "Interview Preparation:",
    text: "Resources and tips to help users prepare for job interviews",
    icon: cdnAssetUrl("assets/img/icon/srv_05.svg"),
  },
  {
    title: "Application Tracking:",
    text: "Tools to help users keep track of their job applications and their status.",
    icon: cdnAssetUrl("assets/img/icon/srv_06.svg"),
  },
];

function ServiceCard({ item }) {
  return (
    <article className="wlj-about-service-card">
      <div className="wlj-about-service-card-inner">
        <div className="wlj-about-service-card-icon">
          <img src={item.icon} alt={item.title} />
        </div>
        <h3>{item.title}</h3>
        <p>{item.text}</p>
        <div className="wlj-about-service-card-shape" aria-hidden="true">
          <img src={cdnAssetUrl("assets/img/shape/srv_shape.png")} alt="" />
        </div>
        <span className="wlj-about-service-card-overlay" aria-hidden="true" />
      </div>
    </article>
  );
}

function AboutUsBody({ formData, onInputChange, onSubmit, submitting, subjects }) {
  const labels = useMarketingLabels();
  const heroRef = useRef(null);
  const [heroTitleVisible, setHeroTitleVisible] = useState(false);

  useEffect(() => {
    const node = heroRef.current;
    if (!node) return undefined;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setHeroTitleVisible(true);
        }
      },
      { threshold: 0.2, rootMargin: "0px 0px -5% 0px" },
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  return (
    <div className="wlj-about-body">
      <section
        ref={heroRef}
        className="wlj-about-hero"
        style={{
          backgroundImage: `url("${cdnAssetUrl("assets/imagesforportal/social-interaction-concept-3d-render.jpg")}")`,
        }}
      >
        <div className="wlj-container wlj-mxw-1710 wlj-about-hero-inner">
          <div className="wlj-about-hero-content">
            <h1 className={`wlj-about-hero-title${heroTitleVisible ? " is-visible" : ""}`}>
              Search And Apply For <br />
              Jobs Globally
            </h1>
          </div>
        </div>
      </section>

      <section className="wlj-about-intro">
        <div className="wlj-container">
          <div className="wlj-about-intro-grid">
            <div className="wlj-about-intro-copy">
              <h2 className="wlj-about-section-title">{labels.aboutUsTitle}</h2>
              <p>
                Nowadays the marketplace and the growing companies are facing real challenges to find
                the right talent. Most importantly, the companies need to understand the facilities
                a global recruitment platform can offer.
              </p>
              <p>
                Welcome to Welinkjobs, the Competitive and interactive network which globally involves
                you to achieve a better opportunity. Moreover in Welinkjobs, companies can immediately
                hire new team members in a competitive advantage through an easy search engine all in
                one platform.
              </p>
              <div className="wlj-about-mission-grid">
                {missionCards.map((item) => (
                  <div key={item.title} className="wlj-about-mission-card">
                    <div className="wlj-about-mission-icon">
                      <img src={item.icon} alt={item.title} />
                    </div>
                    <div className="wlj-about-mission-text">
                      <h3>{item.title}</h3>
                      <p>{item.text}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="wlj-about-intro-image">
              <img src={cdnAssetUrl("assets/img/about/career_growth_concept.png")} alt="About Welinkjobs" />
            </div>
          </div>
        </div>
      </section>

      <section className="wlj-about-spacer" aria-hidden="true" />

      <section
        className="wlj-about-feature"
        style={{ backgroundImage: `url("${cdnAssetUrl("assets/img/bg/feat_bg.jpg")}")` }}
      >
        <div className="wlj-container">
          <div className="wlj-about-feature-grid">
            <div className="wlj-about-feature-copy">
              <span className="wlj-about-kicker">
                <img src={cdnAssetUrl("assets/img/icon/hr_icon.png")} alt="" />
                Find your perfect fit
              </span>
              <h2>
                Let us be
                <br /> the bridge
                <br /> between <br />
                you and your
                <br /> ideal career
                <br />
                opportunity
              </h2>
            </div>
            <div className="wlj-about-feature-list">
              {featureCards.map((item) => (
                <div key={item.title} className={`wlj-about-feature-item ${item.className}`.trim()}>
                  <div className="wlj-about-feature-icon">
                    {item.href ? (
                      <a href={item.href} target="_blank" rel="noreferrer">
                        <img src={item.icon} alt={item.title} />
                      </a>
                    ) : (
                      <img src={item.icon} alt={item.title} />
                    )}
                  </div>
                  {item.href ? (
                    <a href={item.href} target="_blank" rel="noreferrer">
                      <h3>{item.title}</h3>
                    </a>
                  ) : (
                    <h3>{item.title}</h3>
                  )}
                  {item.text ? <p>{item.text}</p> : null}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="wlj-about-fanfact" aria-label={labels.aboutVideo}>
        <div className="wlj-about-fanfact-media">
          <video
            className="wlj-about-fanfact-video"
            src={cdnAssetUrl("assets/img/about/newabout.mp4")}
            loop
            autoPlay
            muted
            playsInline
          />
        </div>
      </section>

      <section className="wlj-about-hotline" aria-label={labels.contactHotline}>
        <div className="wlj-container">
          <div className="wlj-about-hotline-inner">
            <div className="wlj-about-hotline-phone">
              <a href="tel:+13072436345" className="wlj-about-hotline-icon" aria-label={labels.phone}>
                <FontAwesomeIcon icon={faPhone} />
              </a>
              <h3 className="wlj-about-hotline-number">+1 3072436345</h3>
            </div>
            <div className="wlj-about-hotline-whatsapp">
              <p className="wlj-about-hotline-text">
                {labels.hotlineText}
              </p>
              <a
                href="https://wa.me/+13072436345"
                target="_blank"
                rel="noreferrer"
                className="wlj-about-hotline-icon"
                aria-label={labels.whatsApp}
              >
                <FontAwesomeIcon icon={faWhatsapp} />
              </a>
            </div>
          </div>
        </div>
      </section>

      <section className="wlj-about-services">
        <div className="wlj-container">
          <div className="wlj-about-services-grid">
            <div className="wlj-about-services-col">
              <div className="wlj-about-service-lead">
                <h4>Job Search and Application:</h4>
                <p>
                  Users can search for and apply to job opportunities globally through an
                  interactive platform
                </p>
              </div>
              <div className="wlj-about-service-photo">
                <img
                  src={cdnAssetUrl(
                    "assets/imagesforportal/complexities-hrm-through-compelling-graphic-featuring-magnifier-glass-zooming-man.jpg",
                  )}
                  alt="Service feature"
                />
              </div>
              <ServiceCard item={serviceCards[4]} />
            </div>

            <div className="wlj-about-services-col">
              <ServiceCard item={serviceCards[0]} />
              <ServiceCard item={serviceCards[1]} />
              <ServiceCard item={serviceCards[5]} />
            </div>

            <div className="wlj-about-services-col">
              <ServiceCard item={serviceCards[2]} />
              <ServiceCard item={serviceCards[3]} />
            </div>
          </div>
        </div>
        <div className="wlj-about-services-shape wlj-about-services-shape--1" aria-hidden="true">
          <img src={cdnAssetUrl("assets/img/shape/srv_shape1.png")} alt="" />
        </div>
        <div className="wlj-about-services-shape wlj-about-services-shape--2" aria-hidden="true">
          <img src={cdnAssetUrl("assets/img/shape/srv_shape2.png")} alt="" />
        </div>
      </section>

      <section className="wlj-about-contact" id="contactform">
        <div className="wlj-container">
          <div className="wlj-about-contact-wrap">
            <div className="wlj-about-contact-form-col">
              <div className="wlj-about-contact-header">
                <span className="wlj-about-kicker wlj-about-kicker--dark">
                  <img src={cdnAssetUrl("assets/img/icon/hr_icon.png")} alt="" />
                   {labels.getInTouch}
                </span>
                <h2 className="wlj-about-section-title">{labels.contactUs}</h2>
              </div>
              <form className="wlj-about-contact-form" onSubmit={onSubmit}>
                <div className="wlj-about-form-row">
                  <div className="wlj-about-field">
                    <span>
                      <img src={cdnAssetUrl("assets/img/icon/ins_user.svg")} alt="" />
                    </span>
                    <input
                      type="text"
                      name="name"
                      placeholder={labels.contactName}
                      value={formData.name}
                      onChange={onInputChange}
                      required
                    />
                  </div>
                  <div className="wlj-about-field">
                    <span>
                      <img src={cdnAssetUrl("assets/img/icon/ins-sms-tracking.svg")} alt="" />
                    </span>
                    <input
                      type="email"
                      name="email"
                      placeholder={labels.contactEmail}
                      value={formData.email}
                      onChange={onInputChange}
                      required
                    />
                  </div>
                </div>
                <div className="wlj-about-field wlj-about-field--select">
                  <span>
                    <img src={cdnAssetUrl("assets/img/icon/ins-home-hashtag.svg")} alt="" />
                  </span>
                  <select name="subject" value={formData.subject} onChange={onInputChange} required>
                    <option value="">{labels.selectSubject}</option>
                    {subjects.map((subject) => (
                      <option key={subject} value={subject}>
                        {subject}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="wlj-about-field wlj-about-field--textarea">
                  <span>
                    <img src={cdnAssetUrl("assets/img/icon/messages-2.svg")} alt="" />
                  </span>
                  <textarea
                    name="message"
                    rows="10"
                    placeholder={labels.contactMessage}
                    value={formData.message}
                    onChange={onInputChange}
                    required
                  />
                </div>
                <div className="wlj-about-field wlj-about-field--submit">
                  <button type="submit" className="wlj-about-submit" disabled={submitting}>
                    {submitting ? labels.submittingRequest : labels.submitRequest}
                  </button>
                </div>
              </form>
            </div>
            <div className="wlj-about-contact-image">
              <img src={cdnAssetUrl("assets/imagesforportal/contact-1.PNG")} alt="Contact" />
            </div>
          </div>
        </div>
        <div className="wlj-about-contact-shapes" aria-hidden="true">
          <img
            className="wlj-about-contact-shape wlj-about-contact-shape--1"
            src={cdnAssetUrl("assets/img/shape/cnt_shape2.png")}
            alt=""
          />
          <img
            className="wlj-about-contact-shape wlj-about-contact-shape--2"
            src={cdnAssetUrl("assets/img/shape/cnt_shape3.png")}
            alt=""
          />
        </div>
      </section>
    </div>
  );
}

export default AboutUsBody;
